import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setImpersonating(false);
    setOriginalAdmin(null);
  };

  const updatePreferences = async (prefs) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/auth/preferences`,
        prefs,
        { withCredentials: true }
      );
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Update preferences error:", error);
      throw error;
    }
  };

  const impersonateUser = async (userId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/impersonate/${userId}`,
        {},
        { withCredentials: true }
      );
      setOriginalAdmin(user);
      setUser(response.data);
      setImpersonating(true);
      return response.data;
    } catch (error) {
      console.error("Impersonation error:", error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/stop-impersonation`,
        {},
        { withCredentials: true }
      );
      setUser(response.data);
      setImpersonating(false);
      setOriginalAdmin(null);
      return response.data;
    } catch (error) {
      console.error("Stop impersonation error:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      loading, 
      login, 
      logout, 
      checkAuth, 
      updatePreferences, 
      impersonating, 
      originalAdmin, 
      impersonateUser, 
      stopImpersonation, 
      isDemo: user?.is_demo === true,
      isAdmin: user?.is_admin === true,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
